import pRetry from 'p-retry';
import { buildUserIdPayload } from '../utils/userIdPayload';

/**
 * @typedef {Object} TemplateParams
 * @property {string} template_name
 * @property {boolean} created_by_manager
 * @property {number} tariff_code
 * @property {number} selected_mode
 * @property {string} from_address
 * @property {string} to_address
 * @property {string} to_pvz_address
 * @property {number} addition_sum
 * @property {string} sender
 * @property {string} reciver
 * @property {string} services
 * @property {string} places
 * @property {boolean} pay_on_recieve
 * @property {string} who_pay
 * @property {number} city_from
 * @property {number} city_to
 * @property {string} city_from_label
 * @property {string} city_to_label
 * @property {number} vat_rate
 */

/**
 * @typedef {Object} Template
 * @property {number} template_id
 * @property {string} template_name
 * @property {boolean} created_by_manager
 * @property {number} tariff_code
 * @property {number} selected_mode
 * @property {string} from_address
 * @property {string} to_address
 * @property {string} to_pvz_address
 * @property {number} addition_sum
 * @property {string} sender
 * @property {string} reciver
 * @property {string} services
 * @property {string} places
 * @property {boolean} pay_on_recieve
 * @property {string} who_pay
 * @property {number} city_from
 * @property {number} city_to
 * @property {string} city_from_label
 * @property {string} city_to_label
 * @property {number} vat_rate
 * @property {TemplateParams} params
 * @property {string} [contract_number]
 * @property {string} [created_at]
 */

/**
 * @param {{ platform: string, userId: string }} params
 * @returns {Promise<Template[]>}
 */

export const getTemplateByTgId = async ({ platform, userId }) => {
    return await pRetry(
        async () => {
            return await _getTemplateByTgId({ platform, userId });
        },
        { retries: 5 },
    );
};

export const _getTemplateByTgId = async ({ platform, userId }) => {
    const response = await window.fetch(
        `${process.env.REACT_APP_API_URL}/api/template/get_template_by_id`,
        {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(buildUserIdPayload(platform, userId)),
        },
    );

    if (!response.ok) {
        throw new Error(
            `Failed to fetch template by tg_id for ${userId}: ${response.status} ${response.statusText}`,
        );
    }

    const data = await response.json();
    return data.result ?? [];
};

/**
 * @param {{ platform: string, userId: string }} params
 * @returns {Promise<Template[]>}
 */
export const getTemplatesByIdentity = async ({ platform, userId }) => {
    return await pRetry(
        async () => {
            return await _getTemplatesByIdentity({ platform, userId });
        },
        { retries: 5 },
    );
};

export const _getTemplatesByIdentity = async ({ platform, userId }) => {
    const response = await window.fetch(
        `${process.env.REACT_APP_API_URL}/api/template/get_templates_by_identity`,
        {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(buildUserIdPayload(platform, userId)),
        },
    );

    if (!response.ok) {
        throw new Error(
            `Failed to fetch templates by tg_id for ${userId}: ${response.status} ${response.statusText}`,
        );
    }

    const data = await response.json();
    return data.result ?? [];
};

/**
 * @typedef {Object} CreateTemplatePayload
 * @property {string} contract_number
 * @property {TemplateParams} params
 */

/**
 * @param {CreateTemplatePayload} payload
 * @returns {Promise<Template>}
 */
export const createTemplate = async (payload) => {
    const { contract_number, params } = payload;
    const response = await window.fetch(
        `${process.env.REACT_APP_API_URL}/api/template/create_template`,
        {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ contract_number, params }),
        },
    );

    if (!response.ok) {
        throw new Error(
            `Failed to create template: ${response.status} ${response.statusText}`,
        );
    }

    const data = await response.json();
    return data.result;
};

/**
 * @param {number} template_id
 * @returns {Promise<void>}
 */
export const deleteTemplate = async (template_id) => {
    return await pRetry(
        async () => {
            return await _deleteTemplate(template_id);
        },
        { retries: 5 },
    );
};

/**
 * @param {number} template_id
 * @returns {Promise<void>}
 */
export const _deleteTemplate = async (template_id) => {
    const response = await window.fetch(
        `${process.env.REACT_APP_API_URL}/api/template/delete_template`,
        {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ template_id }),
        },
    );

    if (!response.ok) {
        throw new Error(
            `Failed to delete template ${template_id}: ${response.status} ${response.statusText}`,
        );
    }
};
